import { useTranslation } from 'react-i18next'
import { preisgueltigkeitTage, type DocumentKind } from '@symphenia/shared-types'
import {
  FeldStatus,
  SchalterZeile,
  useEntwurf,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'
import { leseZahl } from '../lib/grid'
import { belegartWerte, schreibeBelegart, useBelegartStelle, verteileBelegarten } from '../lib/belegart-stelle'
import type { DocumentSettingsData, KindSetting } from '../types/document.types'

const eingabe =
  'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring aria-[invalid=true]:border-destructive'

const STANDARD_STUFEN = [
  { fee: 0, interestRate: 0, graceDays: 14 },
  { fee: 0, interestRate: 0, graceDays: 10 },
  { fee: 0, interestRate: 0, graceDays: 7 },
]

/** Nachtrag N-21: Preisgültigkeit in Tagen — Angebot und Auftragsbestätigung. */
const GUELTIG_ARTEN: DocumentKind[] = ['OFFER', 'ORDER_CONFIRMATION']

const nichtNegativ = (w: number | null) => (w !== null && (Number.isNaN(w) || w < 0) ? 'Keine negative Zahl' : null)

/**
 * AP-32 7.4/8.5 — **Schwellen der Rechnungsmerkmale und Mahnstufen.**
 *
 * Gebühr, Zinssatz und Schwellen sind Rechtsfragen: Die Vorgabe ist 0 bzw. der
 * Stand der Umsetzung, und das System schlägt nichts vor. Die Schwellen gelten
 * für alle steuerwirksamen Arten gleich — eine Kleinbetragsgrenze, die für die
 * Teilrechnung anders läge als für die Rechnung, gibt es nicht.
 *
 * AP-36 Phase D: ohne Knopf. Die Optionen liegen je Art in `options` und gehen
 * als einzelner Schlüssel (`options.priceValidityDays`), der Server führt einen
 * Schlüssel tief zusammen. Die Mahnstufen sind ein Eintrag (Entwurf) und
 * speichern beim Verlassen der Tabelle.
 */
export function KindOptionsEditor({ daten }: { daten: DocumentSettingsData }) {
  const { t } = useTranslation()
  const art = (k: DocumentKind) => daten.kinds.find((x) => x.kind === k)
  const mahnung = art('DUNNING')
  const lieferschein = art('DELIVERY_NOTE')

  return (
    <div className="space-y-3">
      <div className="grid gap-4 lg:grid-cols-2">
        <Schwellen daten={daten} />
        {mahnung && <Mahnstufen art={mahnung} />}
        <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.options.priceValidity')}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {GUELTIG_ARTEN.map((k) => {
              const a = art(k)
              return a ? <Preisgueltigkeit key={k} art={a} /> : null
            })}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t('documents.options.priceValidityHint')}</p>
        </section>
        {lieferschein && <Lieferschein art={lieferschein} />}
      </div>
      <p className="text-xs text-muted-foreground">{t('documents.options.legalHint')}</p>
    </div>
  )
}

/**
 * Die zwei Schwellen stehen in den Optionen **jeder** steuerwirksamen Art. Eine
 * eigene Stelle mit dem Stand der Rechnung: geprüft wird gegen die Rechnung,
 * die übrigen Arten ziehen ohne Stand nach — sie tragen nur diese zwei
 * Schlüssel, und der Server führt `options` einen Schlüssel tief zusammen.
 */
function Schwellen({ daten }: { daten: DocumentSettingsData }) {
  const { t } = useTranslation()
  const steuerwirksam = daten.kinds.filter((k) => k.capabilities.taxable).map((k) => k.kind)
  const ausRechnung = (antwort: unknown): Datensatz => {
    const r = belegartWerte('INVOICE')(antwort) as Partial<KindSetting>
    return {
      smallInvoiceLimit: r.options?.smallInvoiceLimit ?? 400,
      recipientVatIdFrom: r.options?.recipientVatIdFrom ?? 10000,
      updatedAt: (r as Datensatz).updatedAt,
    }
  }
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'documentThresholds',
    bezeichnung: t('documents.options.thresholds'),
    daten: daten as unknown as Datensatz,
    werte: ausRechnung,
    schreibe: async (patch, stand) => {
      const options = patch
      let antwort = await schreibeBelegart('INVOICE', { options }, stand)
      for (const k of steuerwirksam.filter((x) => x !== 'INVOICE')) antwort = await schreibeBelegart(k, { options })
      verteileBelegarten(antwort)
      return antwort
    },
  }) as Speicherstelle<unknown>
  const betrag = (label: string) => ({ art: 'zahl' as const, stellen: 2, pflicht: true, label, pruefe: nichtNegativ })
  const klein = useFeld<number | null>(stelle, 'smallInvoiceLimit', betrag(t('documents.options.smallInvoiceLimit')))
  const uid = useFeld<number | null>(stelle, 'recipientVatIdFrom', betrag(t('documents.options.recipientVatIdFrom')))

  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.options.thresholds')}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {[
          { f: klein, label: t('documents.options.smallInvoiceLimit') },
          { f: uid, label: t('documents.options.recipientVatIdFrom') },
        ].map(({ f, label }) => (
          <label key={f.props.name} className="space-y-0.5">
            <span className="block text-xs text-muted-foreground">{label}</span>
            <input inputMode="decimal" className={`${eingabe} text-right`} {...f.props} />
            <FeldStatus status={f} />
          </label>
        ))}
      </div>
    </section>
  )
}

type Stufe = { fee: string; interestRate: string; graceDays: string }

/** Die Mahnstufen als ein Eintrag: bearbeitet im Entwurf, gespeichert beim Verlassen der Tabelle. */
function Mahnstufen({ art }: { art: KindSetting }) {
  const { t } = useTranslation()
  const stelle = useBelegartStelle(art.kind, art as unknown as Datensatz, t(`documents.kinds.${art.kind}`))
  const d = useStellenDaten(stelle) as unknown as Partial<KindSetting>
  const gespeichert = (d.options?.dunningLevels?.length ? d.options.dunningLevels : STANDARD_STUFEN).map((s) => ({
    fee: String(s.fee).replace('.', ','),
    interestRate: String(s.interestRate).replace('.', ','),
    graceDays: String(s.graceDays),
  }))
  const zahl = (text: string) => {
    const n = leseZahl(text)
    return n === null || Number.isNaN(n) ? Number.NaN : n
  }
  const eintrag = useEntwurf<Stufe[]>(stelle, {
    kennung: 'dunningLevels',
    gespeichert,
    felder: ['options'],
    label: t('documents.options.dunningLevels'),
    pruefe: (stufen) =>
      stufen.some((s) => [s.fee, s.interestRate, s.graceDays].some((x) => Number.isNaN(zahl(x)) || zahl(x) < 0))
        ? 'Nur Zahlen ab 0'
        : null,
    operation: (stufen) => () => ({
      options: {
        dunningLevels: stufen.map((s) => ({ fee: zahl(s.fee), interestRate: zahl(s.interestRate), graceDays: Math.round(zahl(s.graceDays)) })),
      },
    }),
  })
  const zeilen = eintrag.entwurf ?? gespeichert

  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm" {...eintrag.huelle}>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.options.dunningLevels')}</h2>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th className="py-1" />
            <th className="py-1">{t('documents.options.fee')}</th>
            <th className="py-1">{t('documents.options.interestRate')}</th>
            <th className="py-1">{t('documents.options.graceDays')}</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((s, i) => (
            <tr key={i}>
              <td className="py-1 pr-2 text-xs text-muted-foreground">{t('documents.dunning.level', { level: i + 1 })}</td>
              {(['fee', 'interestRate', 'graceDays'] as const).map((feld) => (
                <td key={feld} className="py-1 pr-2">
                  <input
                    inputMode="decimal"
                    className={`${eingabe} text-right`}
                    value={s[feld]}
                    onChange={(e) => eintrag.setze((alle) => alle.map((x, j) => (j === i ? { ...x, [feld]: e.target.value } : x)))}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <FeldStatus status={eintrag} />
    </section>
  )
}

function Preisgueltigkeit({ art }: { art: KindSetting }) {
  const { t } = useTranslation()
  const stelle = useBelegartStelle(art.kind, art as unknown as Datensatz, t(`documents.kinds.${art.kind}`))
  const label = t('documents.options.priceValidityDays', { kind: t(`documents.kinds.${art.kind}`) })
  const tage = useFeld<number | null | undefined>(stelle, 'options.priceValidityDays', {
    label,
    // Leer heißt „keine Gültigkeit" (null); ohne Eintrag zeigt das Feld die Vorgabe der Art.
    lies: (x) => (x.trim() === '' ? null : (leseZahl(x) ?? Number.NaN)),
    schreib: (w) => {
      const n = w === undefined ? preisgueltigkeitTage(art.kind, {}) : w
      return n === null ? '' : String(n)
    },
    pruefe: (w) => (typeof w === 'number' && (!Number.isInteger(w) || w < 0 || w > 3650) ? '0 bis 3650 Tage' : null),
  })
  return (
    <label className="space-y-0.5">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <input inputMode="numeric" className={`${eingabe} text-right`} {...tage.props} />
      <FeldStatus status={tage} />
    </label>
  )
}

/** N-58: Lieferschein — Verrechnungsablauf fix einhalten. */
function Lieferschein({ art }: { art: KindSetting }) {
  const { t } = useTranslation()
  const stelle = useBelegartStelle(art.kind, art as unknown as Datensatz, t(`documents.kinds.${art.kind}`))
  const fix = useFeld<boolean>(stelle, 'options.requireBillingRelease', { art: 'schalter', label: t('documents.options.requireBillingRelease') })
  return (
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.options.deliveryNote')}</h2>
      <SchalterZeile feld={fix} label={t('documents.options.requireBillingRelease')} hinweis={t('documents.options.requireBillingReleaseHint')} />
    </section>
  )
}
